import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';

import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';
import {
  CertificatePaymentModeEnum,
  type ICertificatePaymentSuccessState,
} from '@share/common-types';
import { FunctionalRootState } from '@share/utils';
import { walletDrawerActions, type ILoginState } from '@share/store/slices';

import { CertificateCard } from '../components/certificate-card';
import { formatExpiryDate, formatUsdDisplay, getPaymentTypeDisplayLabel } from './utils';

import styles from './styles.module.scss';

export const SUPPORT_PHONE = '828-220-6176';

export type CertificatePaymentSuccessVariant = 'template' | 'public';

interface CertificatePaymentSuccessProps {
  variant?: CertificatePaymentSuccessVariant;
}

export function CertificatePaymentSuccess({
  variant = 'template',
}: CertificatePaymentSuccessProps): React.ReactElement | null {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<ICertificatePaymentSuccessState | undefined>();
  const { user, userWallet } = useSelector(
    (state: FunctionalRootState) => state.loginStore,
  ) as ILoginState;

  const state = location.state;
  const isPublicVariant = variant === 'public';

  useEffect(() => {
    if (state?.purchaseResult && state?.certificateTemplate) {
      return;
    }

    const pathname = location.pathname;
    const accountMatch = pathname.match(/^\/([^/]+)\/certificates/);
    const accountUsername = accountMatch?.[1];

    if (isPublicVariant) {
      const tokenMatch = pathname.match(/\/certificates\/public\/payment\/([^/]+)\/success/);
      const token = tokenMatch?.[1];
      if (token && accountUsername) {
        const paymentPath = `/${accountUsername}${Routes.PublicCertificatePayment.replace(
          ':token',
          token,
        )}`;
        history.replace(paymentPath);
        return;
      }
    }

    const path = accountUsername
      ? `/${accountUsername}${Routes.Certificates}`
      : Routes.Certificates;
    history.replace(path);
  }, [state, location.pathname, history, isPublicVariant]);

  const catalogPath = useMemo(() => {
    const pathname = location.pathname;
    const accountMatch = pathname.match(/^\/([^/]+)\/certificates/);
    const accountUsername = accountMatch?.[1];
    return accountUsername
      ? `/${accountUsername}${Routes.CertificatesCatalog}`
      : Routes.CertificatesCatalog;
  }, [location.pathname]);

  const openWallet = useCallback(() => {
    dispatch(walletDrawerActions.setWalletDrawerOpen(true));
  }, [dispatch]);

  const handleBuyAnother = useCallback(() => {
    history.push(catalogPath);
  }, [history, catalogPath]);

  if (!state?.purchaseResult || !state?.certificateTemplate) {
    return null;
  }

  const { purchaseResult, certificateTemplate } = state;
  const loggedUser = user ?? userWallet;
  const firstName = isPublicVariant
    ? state.recipientFirstName?.trim() ?? ''
    : loggedUser?.firstName?.trim() ?? '';
  const expiryDateFormatted = formatExpiryDate(certificateTemplate.validityMonths ?? 0);
  const isInstalmentCheckout =
    state.paymentMode === CertificatePaymentModeEnum.Instalments ||
    state.paymentMode === CertificatePaymentModeEnum.InstalmentsSplitDownPayment;

  const instalmentPlanMonths = state.instalmentPlanMonths;
  const paymentTypeDisplay = getPaymentTypeDisplayLabel(
    intl,
    state.paymentMode,
    instalmentPlanMonths ?? 0,
  );

  const amountPaidDisplay: string | null = isInstalmentCheckout
    ? formatUsdDisplay(purchaseResult.amount)
    : null;

  const thankYouMessageId = isPublicVariant
    ? 'certificates.publicPayment.success.thankYou'
    : 'certificates.success.thankYou';
  const confirmationMessageId = isPublicVariant
    ? 'certificates.publicPayment.success.confirmation'
    : 'certificates.success.confirmation';

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.detailsCard}>
          <div className={styles.header}>
            <span className={styles.iconWrap} aria-hidden>
              <FontAwesomeIcon icon={faCircleCheck} className={styles.icon} />
            </span>
            <div className={styles.headerText}>
              <h1 className={styles.thankYou}>
                <FormattedMessage id={thankYouMessageId} values={{ firstName }} />
              </h1>
              <p className={styles.confirmationMessage}>
                <FormattedMessage
                  id={confirmationMessageId}
                  values={{ certificateName: certificateTemplate.name }}
                />
              </p>
            </div>
          </div>

          <div>
            {isPublicVariant ? (
              <p className={styles.statusLine}>
                <FormattedMessage
                  id="certificates.publicPayment.success.statusLine"
                  values={{ certificateName: certificateTemplate.name }}
                />
              </p>
            ) : (
              <p className={styles.statusLine}>
                <FormattedMessage id="certificates.success.walletBeforeName" />{' '}
                <strong>{certificateTemplate.name}</strong>{' '}
                <FormattedMessage id="certificates.success.walletAfterName" />
              </p>
            )}
            {!isInstalmentCheckout && (
              <p className={styles.statusLine}>
                <FormattedMessage id="certificates.success.expiresLead" />{' '}
                <strong>{expiryDateFormatted}</strong>.
              </p>
            )}
          </div>

          <dl className={styles.transactionDetails}>
            <div className={styles.detailRow}>
              <dt>
                <FormattedMessage id="certificates.success.paymentType" />
              </dt>
              <dd>{paymentTypeDisplay}</dd>
            </div>
            {amountPaidDisplay !== null && (
              <div className={styles.detailRow}>
                <dt>
                  <FormattedMessage id="certificates.success.amountPaid" />
                </dt>
                <dd>{amountPaidDisplay}</dd>
              </div>
            )}
            <div className={styles.detailRow}>
              <dt>
                <FormattedMessage id="certificates.success.refundability" />
              </dt>
              <dd>
                <FormattedMessage id="certificates.success.nonRefundable" />
              </dd>
            </div>
            <div className={styles.detailRow}>
              <dt>
                <FormattedMessage id="certificates.success.support" />
              </dt>
              <dd>
                <a href={`tel:${SUPPORT_PHONE.replace(/\s/g, '')}`} className={styles.supportLink}>
                  {SUPPORT_PHONE}
                </a>
              </dd>
            </div>
          </dl>

          {!isPublicVariant && (
            <div className={styles.actions}>
              <button type="button" className={styles.secondaryButton} onClick={handleBuyAnother}>
                <FormattedMessage id="certificates.success.buyAnother" />
              </button>
              <BlueButton className={styles.primaryButton} onClick={openWallet}>
                <FormattedMessage id="certificates.success.goWallet" />
              </BlueButton>
            </div>
          )}
        </div>

        <aside
          className={`${styles.summaryCard}${
            isPublicVariant ? '' : ` ${styles.summaryCardWithHeaderOffset}`
          }`}
        >
          <CertificateCard
            item={{ ...certificateTemplate, id: purchaseResult.certificateId }}
            imageUrl={certificateTemplate.imageUrl ?? undefined}
            variant="summary"
            summaryTaxAmount={null}
          />
        </aside>
      </div>
    </div>
  );
}
